/**
 * ProgressFooter — design.md §5.8, literally:
 *
 *     "a footer reading `page 7 of 21 · The Sperm Whale · ~14 min left in chapter`
 *      (the §4.6 axis supplies the time)"
 *
 * Three claims on one line, and each one is deliberately scoped to the *chapter*, not the book. A
 * book-wide "page 412 of 1,180" is meaningless in a reader whose pages are columns — it changes
 * when you change the font size, which is precisely the property this design spent its whole
 * pagination model avoiding having to persist. A chapter is a real, stable unit of a book, and
 * "how much of this chapter is left" is the question a person actually has at 11pm.
 *
 * ⚠️ The page number here is **display state, never storage.** §5.1: "Page numbers are never
 * persisted." Nothing may read this component's props back into a position; the position is a sid,
 * and it lives in lib/position.ts.
 */

import type { ReactElement } from "react";
import { estimateSpeechMs } from "../../../shared/narration/rates.ts";

export interface ProgressFooterProps {
  /** 1-based, for display only. In spread mode one "page" is one turn, i.e. two columns. */
  page: number;
  pageCount: number;
  chapterTitle: string;
  /** True when this chapter was split at ingest (`chapters.parts`, §5.1's 60k-char hard splits). */
  partOf?: { index: number; count: number } | null;
  /** Characters between the reader's current sentence and the end of the chapter. */
  charsLeftInChapter: number | null;
  /** Poetry and scripture are read slower; the rate table knows, we just have to tell it. */
  dense?: boolean;
}

export function ProgressFooter(props: ProgressFooterProps): ReactElement {
  const { page, pageCount, chapterTitle, partOf, charsLeftInChapter, dense } = props;
  const progress = pageCount > 1 ? (page - 1) / (pageCount - 1) : 1;

  const bits: string[] = [];
  bits.push(`page ${page} of ${pageCount}`);
  if (chapterTitle) bits.push(partOf && partOf.count > 1 ? `${chapterTitle} (${partOf.index}/${partOf.count})` : chapterTitle);
  const time = timeLeftLabel(charsLeftInChapter, dense === true);
  if (time) bits.push(time);

  return (
    <footer className="ab-footer">
      {/* aria-live="polite" and not "assertive": a screen-reader user turning pages should hear the
          new page number when they pause, not have it interrupt the sentence they are reading. */}
      <span className="ab-footer-line" aria-live="polite">
        {bits.map((b, i) => (
          <span key={b + i}>
            {i > 0 ? <span className="ab-footer-sep" aria-hidden="true">·</span> : null}
            {b}
          </span>
        ))}
      </span>
      {/* Non-interactive on purpose. The chapter scrubber — draggable, and carrying the co-read
          member ticks — is `Scrubber.tsx` and lands with rooms (§5.8, P5). A second draggable
          progress control here would be the same widget built twice, which is the exact failure
          AGENTS.md records about books' three copies of one page grid. */}
      <div className="ab-progress" aria-hidden="true">
        <div className="ab-progress-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
    </footer>
  );
}

/**
 * "~14 min left in chapter".
 *
 * The number comes from `shared/narration/rates.ts` (§2.4 — one rate constant file, because four
 * documents once carried four values for it and the stale one shipped). It is an **estimate** in
 * P1: no clip exists, so there is no measured duration to use, and the tilde is doing honest work.
 * When P2 narrates a chapter, `book_timeline` carries measured milliseconds and this label should
 * switch to them — the estimate is what covers the un-narrated remainder, which is exactly the
 * §4.6 hybrid axis.
 */
function timeLeftLabel(chars: number | null, dense: boolean): string {
  if (chars === null || !Number.isFinite(chars) || chars <= 0) return "";
  const mins = Math.round(estimateSpeechMs(chars, dense) / 60_000);
  if (mins < 1) return "under a minute left";
  return `~${mins} min left in chapter`;
}
